import { NestedUploadRequirementsData, PossibleUploadStates } from '../interfaces/UploadRequirementsInterface';
import SuccessIcon from './assets/data-upload-success.svg';
import WarningIcon from './assets/data-upload-warning-amber.svg';
import EmptyCheckBox from './assets/checkbox-outlined.svg';
import FileTypesValues from '../interfaces/FileType';
import { fileTypes } from '../constants';
import { AxiosResponse } from 'axios';
import LoaderIcon from './assets/data-upload-loader.svg';
import ExternalSchoolTag from './reusable/ExternalSchoolTag';
import useSchools from './hooks/useSchools';

import './SchoolsTable.css';
import useDemographicCategories from './hooks/useDemographicCategories';

const uploadStatuses = [
  { status: 'Uploaded - no errors', src: SuccessIcon, alt: 'check mark icon' },
  { status: 'Uploaded - errors detected', src: WarningIcon, alt: 'warning icon' },
  { status: 'Not yet uploaded', src: EmptyCheckBox, alt: 'empty check box icon' },
  { status: 'Data processing', src: LoaderIcon, alt: 'processing icon' },
];

export type SchoolsTableData = PromiseSettledResult<AxiosResponse<NestedUploadRequirementsData>>;
interface SchoolTableProps {
  data: SchoolsTableData;
  fileType: FileTypesValues;
}

const TEST_SCORES_COLUMNS_KEYS_ORDER = ['SAT', 'ACT'];

export function SchoolsTable({ data, fileType }: SchoolTableProps) {
  const {
    demographicCategories: { data: demographicCategories },
  } = useDemographicCategories();
  const {
    schools: { data: schools },
  } = useSchools();

  if (data.status === 'rejected') return <p className="center-text">Missing upload requirements data!</p>;
  let schoolsData = data.value.data.schools;

  const isExternalSchool = (schoolName: string): boolean => {
    const school = schools?.find((s) => s.name === schoolName);
    return school?.is_primary_on_eoa === false;
  };

  const prepareData = () => {
    let columnKeys: string[] = [];
    let columnNames: string[] = [];
    let tableData: string[][] = [];

    Object.entries(schoolsData).forEach(([schoolName, schoolData]) => {
      if (fileType !== fileTypes.COURSE_CATALOG && isExternalSchool(schoolName)) {
        return;
      }

      if (fileType === fileTypes.STUDENT_DEMOGRAPHICS) {
        columnKeys = demographicCategories?.map((category) => category.name) || [];
        columnNames = demographicCategories?.map((category) => category.display_name) || [];
      } else if (fileType === fileTypes.TEST_SCORES) {
        columnKeys = columnNames = TEST_SCORES_COLUMNS_KEYS_ORDER;
      } else {
        columnKeys = columnNames = Object.keys(schoolData).sort();
      }

      const rowValues = columnKeys.map((key) => schoolData[key]);
      tableData.push([schoolName, ...rowValues]);
    });

    return { columnNames, tableData };
  };

  const { columnNames, tableData } = prepareData();

  return (
    <>
      <div className="upload-statuses-container">
        {uploadStatuses.map(({ status, src, alt }, index) => (
          <div key={index} className="upload-status">
            <img src={src} alt={alt} className="icon-size" />
            <div>{status}</div>
          </div>
        ))}
      </div>
      <table className="table">
        <thead className="table-header">
          <tr className="table-row">
            <th>School</th>
            {columnNames.map((columnName, index) => (
              <th key={index}>{columnName}</th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          {tableData.map((dataRow, index) => (
            <tr key={index} className="table-row">
              {dataRow.map((dataCell, cellIndex) => {
                return cellIndex === 0 ? (
                  <td className="align-left" key={cellIndex}>
                    {dataCell}
                    {isExternalSchool(dataCell) && <ExternalSchoolTag variant="ext" />}
                  </td>
                ) : (
                  <td key={cellIndex}>
                    {dataCell === PossibleUploadStates.UPLOADED && (
                      <img className="icon-size" src={SuccessIcon} alt="check mark icon" />
                    )}
                    {dataCell === PossibleUploadStates.NOT_YET_UPLOADED && (
                      <img className="icon-size" src={EmptyCheckBox} alt="empty check box icon" />
                    )}
                    {dataCell === PossibleUploadStates.UPLOADED_WITH_ERRORS && (
                      <img className="icon-size" src={WarningIcon} alt="warning icon" />
                    )}
                    {dataCell === PossibleUploadStates.UPLOAD_IN_PROGRESS && (
                      <img className="icon-size" src={LoaderIcon} alt="processing icon" />
                    )}
                    {dataCell === PossibleUploadStates.NOT_APPLICABLE && <span>N/A</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default SchoolsTable;
